import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { apiHelpers } from '../pages/DashboardUtils';

// 创建上下文
const PrivateKnowledgeBaseContext = createContext();

export const usePrivateKnowledgeBase = () => {
  const context = useContext(PrivateKnowledgeBaseContext);
  if (!context) {
    throw new Error('usePrivateKnowledgeBase must be used within a PrivateKnowledgeBaseProvider');
  }
  return context;
};

export const PrivateKnowledgeBaseProvider = ({ children }) => {
  const { t } = useTranslation(['messages']);
  
  // Private KB related state
  const [privateTopics, setPrivateTopics] = useState([]);
  const [privateKBInfo, setPrivateKBInfo] = useState(null);
  const [hasPrivateKB, setHasPrivateKB] = useState(false);
  const [expandedTopicsArray, setExpandedTopicsArray] = useState([]);
  const [editingTopic, setEditingTopic] = useState(null);
  const [editingTopicName, setEditingTopicName] = useState('');
  const [editingTopicContext, setEditingTopicContext] = useState(null);
  const [topicNotesCount, setTopicNotesCount] = useState({});

  // 创建兼容的展开状态对象，提供 has 方法
  const expandedTopics = useMemo(() => ({
    has: (id) => expandedTopicsArray.includes(id),
    add: (id) => setExpandedTopicsArray(prev => prev.includes(id) ? prev : [...prev, id]),
    delete: (id) => setExpandedTopicsArray(prev => prev.filter(item => item !== id)),
    clear: () => setExpandedTopicsArray([]),
    size: expandedTopicsArray.length,
    forEach: (callback) => expandedTopicsArray.forEach(callback),
    values: () => expandedTopicsArray.values(),
    entries: () => expandedTopicsArray.map(item => [item, item]),
    keys: () => expandedTopicsArray.values(),
    [Symbol.iterator]: function* () {
      yield* expandedTopicsArray;
    }
  }), [expandedTopicsArray]);

  // 递归获取主题树中所有主题的笔记数量
  const fetchTopicNotesCountForTree = useCallback(async (topics) => {
    const allTopicIds = [];
    
    // 递归收集所有主题ID
    const collectTopicIds = (topicList) => {
      topicList.forEach(topic => {
        allTopicIds.push(topic.topic_id);
        if (topic.children && topic.children.length > 0) {
          collectTopicIds(topic.children);
        }
      });
    };
    
    collectTopicIds(topics);
    
    if (allTopicIds.length > 0) {
      console.log(`[前端] 开始获取 ${allTopicIds.length} 个主题的笔记数量...`);
      const countsMap = await apiHelpers.fetchMultipleTopicNotesCount(allTopicIds);
      setTopicNotesCount(prevCounts => ({
        ...prevCounts,
        ...countsMap
      }));
      console.log(`[前端] 主题笔记数量更新完成:`, countsMap);
    }
  }, []);

  // 获取私有笔记库主题树
  const fetchPrivateKBTopics = useCallback(async () => {
    try {
      const result = await apiHelpers.fetchPrivateKBTopics();
      setHasPrivateKB(result.hasPrivateKB);
      setPrivateTopics(result.topics);
      setPrivateKBInfo(result.knowledgeBase);
      console.log('设置私有笔记库主题:', result.topics, '知识库信息:', result.knowledgeBase);
      
      // 获取主题的笔记数量
      if (result.topics && result.topics.length > 0) {
        await fetchTopicNotesCountForTree(result.topics);
      }
      
      return result;
    } catch (error) {
      console.error('获取私有库主题失败:', error);
      throw error;
    }
  }, [fetchTopicNotesCountForTree]);

  // 计算私有库的总笔记数量
  const calculatePrivateKBNotesCount = useCallback(() => {
    if (!privateTopics || privateTopics.length === 0) {
      return 0;
    }

    // 只累加Level 0主题的笔记数量（从topicNotesCount缓存中获取）
    return privateTopics.reduce((total, topic) => {
      return total + (topicNotesCount[topic.topic_id] || 0);
    }, 0);
  }, [privateTopics, topicNotesCount]);

  // 切换主题展开/收起状态
  const toggleTopicExpanded = useCallback((topicId) => {
    setExpandedTopicsArray(prev => {
      if (prev.includes(topicId)) {
        return prev.filter(id => id !== topicId);
      } else {
        return [...prev, topicId];
      }
    });
  }, []);

  // 初始化私有笔记库
  const handleInitializePrivateKB = useCallback(async () => {
    try {
      console.log('[handleInitializePrivateKB] 开始初始化私有笔记库');
      const response = await apiHelpers.initializePrivateKB();
      
      console.log('[handleInitializePrivateKB] 私有笔记库初始化成功:', response);
      
      // 显示成功消息
      message.success(t('messages:private_kb_init_success', { defaultValue: 'Private knowledge base initialized successfully' }));
      
      // 重新获取私有库数据
      console.log('[handleInitializePrivateKB] 重新获取私有库数据');
      await fetchPrivateKBTopics();
      
      // 刷新整个页面
      console.log('[handleInitializePrivateKB] 初始化成功，刷新整个页面');
      window.location.reload();
    } catch (error) {
      console.error('[handleInitializePrivateKB] 私有笔记库初始化失败:', error);
      
      // 提取错误消息
      let errorMessage = t('messages:unknown_error', { defaultValue: 'Unknown error' });
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(t('messages:private_kb_init_failed', { defaultValue: 'Private knowledge base initialization failed: {{error}}', error: errorMessage }));
    }
  }, [fetchPrivateKBTopics]);

  // 在树中查找主题
  const findTopicInTree = useCallback((topics, targetId) => {
    if (!topics) return null;
    
    for (const topic of topics) {
      if (topic.topic_id === targetId) {
        return topic;
      }
      if (topic.children && topic.children.length > 0) {
        const found = findTopicInTree(topic.children, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // 检查主题是否属于私有库
  const isPrivateTopic = useCallback((topicId) => {
    return privateTopics && findTopicInTree(privateTopics, topicId);
  }, [privateTopics, findTopicInTree]);

  const value = {
    // State
    privateTopics,
    privateKBInfo,
    hasPrivateKB,
    expandedTopics,
    editingTopic,
    editingTopicName,
    editingTopicContext,
    topicNotesCount,
    
    // State setters
    setPrivateTopics,
    setPrivateKBInfo,
    setHasPrivateKB,
    setExpandedTopicsArray,
    setEditingTopic,
    setEditingTopicName,
    setEditingTopicContext,
    setTopicNotesCount,
    
    // Functions
    fetchPrivateKBTopics,
    fetchTopicNotesCountForTree,
    calculatePrivateKBNotesCount,
    toggleTopicExpanded,
    handleInitializePrivateKB,
    findTopicInTree,
    isPrivateTopic
  };

  return (
    <PrivateKnowledgeBaseContext.Provider value={value}>
      {children}
    </PrivateKnowledgeBaseContext.Provider>
  );
};